<template>
  <div class="page-content">
    <div class="title">
      Price Lists Manager
    </div>

    <!-- Generate button + status -->
    <div class="flex items-center gap-4 mb-6">
      <button
        class="btn btn-dark-button"
        :disabled="generating"
        @click="generate"
      >
        <span v-if="generating === 'all'">Generating...</span>
        <span v-else>Generate All Price Lists</span>
      </button>

      <div v-if="generateResult" class="text-sm">
        <span class="text-green-success font-semibold">
          ✓ {{ generateResult.success }} generated
        </span>
        <span
          v-if="generateResult.errors > 0"
          class="text-red-cancel ml-2 font-semibold"
        >
          ✗ {{ generateResult.errors }} errors
        </span>
      </div>

      <div v-if="generateError" class="text-red-cancel text-sm">
        {{ generateError }}
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex justify-center py-8">
      <div
        class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800"
      ></div>
    </div>

    <!-- Error -->
    <div
      v-else-if="loadError"
      class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4"
    >
      {{ loadError }}
    </div>

    <!-- Empty -->
    <div v-else-if="priceLists.length === 0" class="text-gray-500 py-4">
      No price lists found. Click "Generate All Price Lists" to create them.
    </div>

    <!-- Table -->
    <div v-else class="overflow-x-auto">
      <table
        class="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden"
      >
        <thead class="bg-gray-50">
          <tr>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Make
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Download
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Products
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Last Updated
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200">
          <tr
            v-for="item in priceLists"
            :key="item.id"
            class="hover:bg-gray-50"
          >
            <td class="px-4 py-3 font-medium text-gray-800">
              {{ item.make }}
            </td>
            <td class="px-4 py-3">
              <a
                :href="item.url"
                target="_blank"
                rel="noopener noreferrer"
                class="text-blue-600 hover:underline text-sm"
              >
                Download
              </a>
            </td>
            <td class="px-4 py-3 text-sm text-gray-500">
              {{ item.productsExported != null ? item.productsExported : "—" }}
            </td>
            <td class="px-4 py-3 text-sm text-gray-500">
              {{ item.updatedAt }}
            </td>
            <td class="px-4 py-3">
              <button
                class="btn btn-dark-button text-xs px-3 py-1"
                :disabled="generating === item.make"
                @click="generateForMake(item.make)"
              >
                <span v-if="generating === item.make">Generating...</span>
                <span v-else>Regenerate</span>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import {
  getPriceLists,
  generatePriceLists,
  generatePriceListForMake
} from "@/api/store/PriceListsService.js";

export default {
  name: "PriceListManager",
  data() {
    return {
      priceLists: [],
      loading: true,
      loadError: null,
      generating: null, // null | 'all' | '<make>'
      generateResult: null,
      generateError: null
    };
  },
  mounted() {
    this.loadPriceLists();
  },
  methods: {
    async loadPriceLists() {
      this.loading = true;
      this.loadError = null;
      try {
        this.priceLists = await getPriceLists();
      } catch (e) {
        this.loadError = "Failed to load price lists. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    async generate() {
      this.generating = "all";
      this.generateResult = null;
      this.generateError = null;
      try {
        this.generateResult = await generatePriceLists();
        await this.loadPriceLists();
      } catch (e) {
        this.generateError = "Generation failed. Please try again.";
      } finally {
        this.generating = null;
      }
    },
    async generateForMake(make) {
      this.generating = make;
      this.generateResult = null;
      this.generateError = null;
      try {
        this.generateResult = await generatePriceListForMake(make);
        await this.loadPriceLists();
      } catch (e) {
        this.generateError = `Generation failed for ${make}. Please try again.`;
      } finally {
        this.generating = null;
      }
    }
  }
};
</script>
